import request from './request'

// API接口集合
const api = {
  /**
   * 获取轮播图列表
   * @param {Object} params - 请求参数
   * @param {string} params.type - 轮播图类型，如'index'
   * @returns {Promise<Object>} - 返回轮播图数据
   */
  getBannerList(params) {
    return request.get('/api/banner/lists', params)
  },

  /**
   * 获取漫画列表
   * @param {Object} params - 请求参数
   * @returns {Promise<Object>} - 返回漫画列表数据
   */
  getComicList(params) {
    return request.get('/api/comic/comics', params)
  },

  /**
   * 用户登录
   * @param {Object} data - 请求参数
   * @param {string} data.account - 用户账号
   * @param {string} data.password - 用户密码
   * @returns {Promise<Object>} - 返回登录结果
   */
  login(data) {
    return request.post('/api/user/login', data)
  },

  /**
   * 用户注册
   * @param {Object} data - 请求参数
   * @param {string} data.username - 用户名
   * @param {string} data.password - 用户密码
   * @returns {Promise<Object>} - 返回注册结果
   */
  register(data) {
    return request.post('/api/user/register', data)
  },

  /**
   * 获取用户信息
   * @returns {Promise<Object>} - 返回用户信息
   */
  getUserInfo() {
    return request.get('/api/user/index')
  },

  /**
   * 修改个人资料（昵称/性别/生日/简介/头像）
   * @param {Object} data - nickname / gender / birthday / bio / avatar
   */
  updateProfile(data) {
    return request.post('/api/user/profile', data)
  },

  /**
   * 修改密码（需要当前密码，成功后需重新登录）
   * @param {Object} data - oldpassword / newpassword
   */
  changePassword(data) {
    return request.post('/api/user/changepwd', data)
  },

  /**
   * 上传文件（头像等），multipart/form-data
   * @param {File} file - 文件对象
   */
  uploadFile(file) {
    const formData = new FormData()
    formData.append('file', file)
    return request.upload('/api/common/upload', formData)
  },
  
  /**
   * 获取漫画详情
   * @param {number|string} id - 漫画ID
   * @returns {Promise<Object>} - 返回漫画详情
   */
  getComicDetail(id) {
    return request.get('/api/comic/detail', { id })
  },

  /**
   * 获取漫画章节列表
   * @param {number|string} comic_id - 漫画ID
   * @returns {Promise<Object>} - 返回章节列表数据
   */
  getComicChapters(comic_id) {
    return request.get('/api/comic/chapters', { comic_id })
  },

  /**
   * 获取章节详情
   * @param {Number} id 章节ID
   */
  getChapterDetail(id) {
    return request.get('/api/comic/chapter_detail', { id })
  },

  /**
   * 获取VIP套餐列表
   * @returns {Promise<Object>} - 返回VIP套餐列表数据
   */
  getVipPackages() {
    return request.get('/api/comic/vip_packages')
  },

  /**
   * 获取阅读历史
   * @returns {Promise<Object>} - 返回阅读历史数据
   */
  getReadingHistory() {
    return request.get('/api/user/reading_history')
  },

  /**
   * 删除阅读历史
   * @param {number|string} history_id - 历史记录ID(可选)，不传则删除所有历史
   * @returns {Promise<Object>} - 返回删除结果
   */
  deleteReadingHistory(history_id) {
    return request.post('/api/user/delete_history', history_id ? { history_id } : {})
  },

  /**
   * 获取关于我们信息
   * @returns {Promise<Object>} - 返回关于我们信息
   */
  getAboutUs() {
    return request.get('/api/about')
  },

  /**
   * 搜索漫画
   * @param {string} keyword - 关键词
   * @returns {Promise<Object>} - 返回搜索结果
   */
  searchComic(keyword) {
    return request.get('/api/comic/search', { keyword })
  },

  /**
   * 获取漫画评论列表
   * @param {number|string} comic_id - 漫画ID
   */
  getComicComments(comic_id, params = {}) {
    return request.get('/api/comic/comments', { comic_id, ...params })
  },

  /**
   * 发表漫画评论（需登录）
   * @param {Object} data - comic_id / content
   */
  submitComicComment(data) {
    return request.post('/api/comic/comment', data)
  },

  /**
   * 删除自己的漫画评论
   * @param {number|string} comment_id - 评论ID
   */
  deleteComicComment(comment_id) {
    return request.post('/api/comic/delete_comment', { comment_id })
  },

  /**
   * 获取我的评论列表（含漫画标题与封面）
   */
  getMyComments() {
    return request.get('/api/comic/my_comments')
  },

  /**
   * 评论点赞/取消点赞
   * @param {number|string} comment_id - 评论ID
   */
  likeComment(comment_id) {
    return request.post('/api/comic/like_comment', { comment_id })
  },

  /**
   * 获取排行榜
   * @param {string} type - hot=阅读热榜 / favorite=收藏榜 / new=新作榜
   */
  getRanking(type, limit) {
    return request.get('/api/comic/ranking', { type, limit: limit || 20 })
  },

  /**
   * 获取收藏状态
   */
  getFavoriteStatus(comic_id) {
    return request.get('/api/comic/check_favorite', { comic_id })
  },

  /**
   * 收藏漫画
   */
  favoriteComic(comic_id) {
    return request.post('/api/comic/favorite', { comic_id })
  },

  /**
   * 取消收藏
   */
  unfavoriteComic(comic_id) {
    return request.post('/api/comic/unfavorite', { comic_id })
  },

  /**
   * 我的收藏列表
   */
  getMyFavorites() {
    return request.get('/api/comic/my_favorites')
  },

  /**
   * 保存阅读进度（章节内页码）
   */
  saveProgress(data) {
    return request.post('/api/comic/save_progress', data)
  },

  /**
   * 获取阅读进度（章节内页码）
   */
  getProgress(comic_id, chapter_id) {
    return request.get('/api/comic/progress', { comic_id, chapter_id })
  },

  /**
   * 社区帖子列表
   */
  getCommunityPosts(params = {}) {
    return request.get('/api/community/posts', params)
  },

  /**
   * 发布社区帖子（需登录）
   */
  publishCommunityPost(data) {
    return request.post('/api/community/publish', data)
  },

  /**
   * 社区帖子点赞/取消点赞
   */
  likeCommunityPost(post_id) {
    return request.post('/api/community/like', { post_id })
  },

  /**
   * 社区帖子浏览量 +1
   */
  viewCommunityPost(post_id) {
    return request.post('/api/community/view', { post_id })
  },

  /**
   * 社区帖子评论列表
   */
  getPostComments(post_id) {
    return request.get('/api/community/post_comments', { post_id })
  },

  /**
   * 发表社区帖子评论（需登录）
   */
  submitPostComment(data) {
    return request.post('/api/community/post_comment', data)
  },

  /**
   * 删除自己的社区帖子评论
   */
  deletePostComment(comment_id) {
    return request.post('/api/community/delete_post_comment', { comment_id })
  },

  /**
   * 提交举报
   */
  submitReport(data) {
    return request.post('/api/report/submit', data)
  },

  /**
   * 我的消息列表
   */
  getMessages() {
    return request.get('/api/message/index')
  },

  /**
   * 标记消息已读（不传 id 标记全部）
   */
  markMessagesRead(id) {
    return request.post('/api/message/read', id ? { id } : {})
  },

  /**
   * 未读消息数
   */
  getUnreadCount() {
    return request.get('/api/message/unread')
  },

  /**
   * 购买/开通记录
   */
  getPurchaseHistory() {
    return request.get('/api/user/purchase_history')
  },

  /**
   * 提交找回密码申请
   */
  submitForgotPassword(data) {
    return request.post('/api/user/forgot_password', data)
  },

  /**
   * 获取当前账号的青少年模式状态（账号绑定，需登录）
   * @returns {Promise<Object>} - 返回设置与倒计时状态
   */
  getYouthStatus() {
    return request.get('/api/youth/index')
  },

  /**
   * 保存青少年模式权限设置
   * @param {Object} data - guardianPhone / contentLevel / rules / guardianCode / currentCode
   */
  saveYouthSettings(data) {
    return request.post('/api/youth/save', data)
  },

  /**
   * 开启青少年模式（需要密码确认；首次开启时以该密码建立监护人密码）
   * @param {string} guardianCode - 监护人密码
   */
  enableYouthMode(guardianCode) {
    return request.post('/api/youth/enable', { guardianCode })
  },

  /**
   * 密码认证，关闭青少年模式（永久关闭）
   * @param {string} guardianCode - 监护人密码
   */
  authYouthMode(guardianCode) {
    return request.post('/api/youth/auth', { guardianCode })
  },

  /**
   * 恢复默认青少年模式配置
   * @param {string} guardianCode - 模式开启且已设密码时需传入
   */
  resetYouthSettings(guardianCode) {
    return request.post('/api/youth/reset', guardianCode ? { guardianCode } : {})
  },

  /**
   * 提交实名认证（真实姓名 + 身份证号，后台实名前台自愿）
   * @param {Object} data - { name, idcard }
   */
  submitRealname(data) {
    return request.post('/api/realname/submit', data)
  },

  /**
   * 查询当前账号实名状态
   * @returns {Promise<Object>} - { verified, is_minor, age, masked_name }
   */
  getRealnameStatus() {
    return request.get('/api/realname/status')
  }
}

export default api 